import React, { useState, useEffect } from "react";
import styles from "./ContactUsForm.module.scss";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import ElchemyLogoSVG from "./ElchemyLogoSVG";
import { submitContactForm } from "../../../api/FormsApi";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  trackFormLoaded,
  trackFormSubmitted,
  FORM_TYPES,
  getPageType,
} from "@/utils/gtmEvents";
import {
  showToast,
  TOAST_TYPE,
  FORM_TOAST_CONTAINER_ID,
} from "@/components/Common/Toast";

/** App-wide form-toast container id; the single container lives in Layout. */
const TOAST_CONTAINER_ID = FORM_TOAST_CONTAINER_ID;

// Validation schema
const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required"),
  email: Yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Enter a valid email address"
    )
    .required("Email is required"),
  mobileNumber: Yup.string().typeError(
    "That doesn't look like a phone number"
  ),
  message: Yup.string()
    .trim()
    .required("Message is required")
    .max(500, "Message exceeds 500 characters"),
  recaptcha: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
    ? Yup.string().required("Please complete the reCAPTCHA")
    : Yup.string().nullable(),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must agree to the Terms & Conditions"
  ),
});

function ContactUsForm({
  noSection = false,
  onClose = () => {},
  customLogo = null,
  customText = null,
  headingPrefix = null,
  headingHighlight = "Streamline",
  // Catalogue id of the product this form sits under, sent as `product` in the
  // payload so an enquiry can be attributed to what the user was looking at.
  //
  // ⚠️ OPT-IN. This component is rendered by 14 pages; omitted (the default) the
  // key is left out of the request entirely, so every existing consumer sends a
  // byte-identical body. Only single-product contexts pass it.
  product = null,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const pathname = usePathname();

  // Track FORM_LOADED event on mount
  useEffect(() => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const pageType = getPageType(pathname);
    trackFormLoaded(FORM_TYPES.CONTACT_US, pageUrl, pageType);
  }, [pathname]);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobileNumber: "",
      message: "",
      recaptcha: null,
      termsAccepted: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      // console.log("Submitting form with values:", values);
      setIsLoading(true);

      try {
        const formData = {
          name: values.name,
          email: values.email,
          mobileNumber: values.mobileNumber,
          country: "United States", // Default for now, can be extracted from phone input later
          message: values.message,
          recaptcha: values.recaptcha,
          // Spread rather than assigned, so consumers that pass no `product` send a
          // request body with no `product` key at all — not `product: null`. Keeps
          // the 14 existing callers byte-identical.
          ...(product ? { product } : {}),
        };

        const result = await submitContactForm(formData);

        if (result) {
          // Track FORM_SUBMITTED event
          const pageUrl = typeof window !== "undefined" ? window.location.href : "";
          const pageType = getPageType(pathname);
          trackFormSubmitted(FORM_TYPES.CONTACT_US, pageUrl, pageType);

          // console.log("Form submitted successfully:", result);
          formik.resetForm();
          window?.grecaptcha?.reset();
          showToast("Request Submitted, We will get back soon!", {
            type: TOAST_TYPE.SUCCESS,
            containerId: TOAST_CONTAINER_ID,
          });
          if (noSection) {
            onClose();
          }
        } else {
          showToast("Something went wrong, please try again!", {
            type: TOAST_TYPE.ERROR,
            containerId: TOAST_CONTAINER_ID,
          });
        }
      } catch (error) {
        showToast("Something went wrong, please try again!", {
          type: TOAST_TYPE.ERROR,
          containerId: TOAST_CONTAINER_ID,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handlePhoneFocus = () => {
    setIsPhoneFocused(true);
  };

  const handlePhoneBlur = () => {
    setIsPhoneFocused(false);
    formik.handleBlur("mobileNumber");
  };

  return (
    <div
      className={styles.contactUsFormSection}
      style={noSection ? { padding: 0 } : {}}
    >
      <div
        className={styles.contactUsForm}
        style={!noSection ? { borderRadius: "24px" } : {}}
      >
        {/* Radial Gradient Overlay */}
        <div className={styles.radialGradientOverlay}></div>
        {/* Left Side */}
        <div className={styles.formHeadingContainer}>
          {/* Elchemy Logo */}
          {customLogo ? (
            <div className={styles.customLogoContainer}>
              <Image
                src={customLogo}
                alt="Elchemy Logo"
                width={200}
                height={60}
                className={styles.customLogo}
              />
            </div>
          ) : (
            <ElchemyLogoSVG />
          )}
          {/* Form Heading */}
          <h2>
            {headingPrefix ? `${headingPrefix} ` : null}
            <span>{headingHighlight}</span> <br />
            {customText || "Your Chemical"} <br /> Procurement
          </h2>
          {/* Form Subheading */}
          <p>Get fast responses from real experts</p>
        </div>
        {/* Right Side */}
        <div className={styles.formContainer}>
          {/* Form */}
          <form onSubmit={formik.handleSubmit}>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className={styles.errorMessage}>{formik.errors.name}</div>
            )}

            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className={styles.errorMessage}>{formik.errors.email}</div>
            )}

            {/* Phone Input */}
            <div
              className={`${styles.phoneInputContainer} ${
                isPhoneFocused ? styles.phoneFocused : ""
              }`}
            >
              <PhoneInput
                country={"us"}
                enableSearch={true}
                value={formik.values.mobileNumber}
                onChange={(value) => {
                  formik.setFieldValue("mobileNumber", value);
                }}
                onFocus={handlePhoneFocus}
                onBlur={handlePhoneBlur}
                placeholder="Mobile Number"
                containerClass={styles.phoneInputWrapper}
                inputClass={styles.phoneInput}
                buttonClass={styles.phoneInputButton}
                dropdownClass={styles.phoneInputDropdown}
                searchClass={styles.phoneInputSearch}
                containerStyle={{
                  width: "100%",
                  height: "44px",
                }}
                inputStyle={{
                  width: "100%",
                  height: "44px",
                  fontSize: "16px",
                  fontFamily: "var(--font-open-sans), sans-serif",
                  backgroundColor: "rgba(24, 6, 5, 0.5)",
                  color: "rgba(170, 168, 167, 1)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "0 8px 8px 0",
                  borderLeft: "none",
                  paddingLeft: "68px",
                  outline: "none",
                }}
                buttonStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.5)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "8px 0 0 8px",
                  borderRight: "none",
                  width: "60px",
                  padding: "0 12px 0 8px",
                  outline: "none",
                }}
                dropdownStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.95)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "8px",
                  zIndex: 10,
                  maxHeight: "200px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                }}
                searchStyle={{
                  backgroundColor: "rgba(24, 6, 5, 0.8)",
                  border: "1px solid rgba(106, 102, 100, 1)",
                  borderRadius: "6px",
                  color: "rgba(170, 168, 167, 1)",
                  padding: "8px 12px",
                  margin: "8px",
                  fontFamily: "var(--font-open-sans), sans-serif",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                <div className={styles.errorMessage}>
                  {formik.errors.mobileNumber}
                </div>
              )}
            </div>

            <textarea
              id="message"
              name="message"
              placeholder="Message"
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.message && formik.errors.message && (
              <div className={styles.errorMessage}>{formik.errors.message}</div>
            )}

            {/* Terms and conditions checkbox */}
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="termsAccepted"
                name="termsAccepted"
                checked={formik.values.termsAccepted}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <label htmlFor="termsAccepted">
                <span>By submitting, I agree with&nbsp;</span>
                <Link className="hyperlink" href="/terms-and-conditions">
                  <span className="underline">Terms & Conditions</span>
                </Link>
              </label>
            </div>
            {formik.touched.termsAccepted && formik.errors.termsAccepted && (
              <div className={styles.errorMessage}>
                {formik.errors.termsAccepted}
              </div>
            )}

            {/* reCAPTCHA */}
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
              <div className={styles.recaptchaContainer}>
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    formik.setFieldValue("recaptcha", value);
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.recaptcha && formik.errors.recaptcha && (
                  <div className={styles.errorMessage}>
                    {formik.errors.recaptcha}
                  </div>
                )}
              </div>
            )}

            {/* Form Submit Button */}
            <button
              type="submit"
              disabled={
                isLoading ||
                !formik.isValid ||
                !formik.values.termsAccepted ||
                (false &&
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY &&
                  !formik.values.recaptcha)
              }
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ContactUsForm;
